<template>
	<div class="product-form">
		<div class="mx-5 mb-5">
			<v-form ref="generalForm" v-model="generalFormValid">
				<v-row no-gutters class="mt-7" v-if="!isExternalEntity">
					<b v-translate>Type:</b>
					<v-btn-toggle class="ml-2" v-model="type" mandatory>
						<v-btn value="compost" small class="text-overline" :disabled="edit">
							<translate>Product</translate>
						</v-btn>
						<v-btn value="substance" small class="text-overline" :disabled="edit">
							<translate>Substance</translate>
						</v-btn>
						<v-btn value="complex_substance" small class="text-overline" :disabled="edit">
							<translate>Complex Substance</translate>
						</v-btn>
						<v-btn value="complex_product" small class="text-overline" :disabled="edit">
							<translate>Complex Product</translate>
						</v-btn>
					</v-btn-toggle>
					<template v-if="!admin">
						<v-alert v-model="success" color="alert" type="success" class="ml-3">
							<translate>Saved Successfully</translate>
						</v-alert>
						<v-alert v-model="error" color="red" type="error" class="ml-3">
							<translate>Error</translate>
							<span>{{errorMessage}}</span>
						</v-alert>
					</template>
				</v-row>
				<v-row no-gutters v-if="admin || isExternalEntity" class="mt-2">
					<b v-translate>Language:</b>
					<v-btn-toggle class="ml-2" v-model="language" mandatory>
						<v-btn value="pt" small class="text-overline" key="pt">
							<strong class="mr-1">PT</strong>
							<span v-translate>Portuguese</span>
						</v-btn>
						<v-btn value="en" small class="text-overline" key="en">
							<strong class="mr-1">EN</strong>
							<span v-translate>English</span>
						</v-btn>
						<v-btn value="es" small class="text-overline" key="es">
							<strong class="mr-1">ES</strong>
							<span v-translate>Spanish</span>
						</v-btn>
					</v-btn-toggle>
					<v-spacer></v-spacer>
					<v-alert v-model="success" color="alert" type="success">
						<translate>Saved Successfully</translate>
					</v-alert>
					<v-alert v-model="error" color="red" type="error">
						<translate>Error</translate>
						<span>{{errorMessage}}</span>
					</v-alert>
				</v-row>
				<v-row no-gutters v-if="admin || isExternalEntity">
					<v-checkbox
						v-model="product.revision"
						color="primary"
						:label="$gettext('Rever')"
						class="mt-0 pb-0"
						hide-details
					/>
					<v-checkbox
						v-model="product.discontinued"
						color="primary"
						:label="$gettext('Discontinued')"
						class="mt-0 pb-0 ml-2"
						hide-details
					/>
					<v-checkbox
						v-model="product.visible"
						color="primary"
						:label="$gettext('Visible')"
						class="mt-0 pb-0 mx-2"
						hide-details
					/>
					<v-btn
						v-if="edit && product.substance"
						small
						class="text-overline mt-1 ml-1"
						color="primary"
						@click="getAssociatedProducts"
					>
						<span v-translate>Associated Products</span>
						<v-dialog v-model="showAssociatedProducts" max-width="30%">
							<v-card>
								<v-card-title class="title primary--text mb-2 secondary">
									<translate>Associated Products</translate>
								</v-card-title>
								<v-card-text>
									<v-row class="my-1" no-gutters v-for="p in associatedProducts" :key="p.name">
										<strong v-translate>Name:</strong>
										{{p.name}}
										<v-spacer></v-spacer>
										<strong v-translate>Space:</strong>
										{{p.space_id}}
									</v-row>
								</v-card-text>
								<v-divider></v-divider>
								<v-card-actions>
									<v-spacer></v-spacer>
									<v-btn color="primary darken-1" text @click="showAssociatedProducts = false">
										<translate>Close</translate>
									</v-btn>
								</v-card-actions>
							</v-card>
						</v-dialog>
					</v-btn>
					<v-btn
						v-if="type != 'complex_product'"
						small
						class="text-overline mt-1 ml-1"
						color="primary"
						@click="clickImport"
						:loading="saving"
					>
						<span v-if="type == 'substance' || type == 'complex_substance'" v-translate key="substance">Import Substance</span>
						<span v-if="type == 'compost'" v-translate key="product">Import Product</span>
					</v-btn>
					<v-row v-if="showImportProduct && type == 'compost'" no-gutters>
						<v-col cols="12" md="8" class="px-2">
							<SearchProducts context="new-product" type="supplement" method="get" @click="importProduct" />
						</v-col>
					</v-row>
					<v-row v-if="showImportSubstance && (type == 'substance' || type == 'complex_substance')" no-gutters>
						<v-col cols="12" md="8" class="px-2">
							<SearchProducts v-if="importSubstanceType == 'fito'" context="new-product" :substance="true" type="supplement" method="get" @click="importProduct" />
							<SearchSubstances v-else type="formula" method="get" @click="importProduct" />
						</v-col>
						<v-col cols="4" class="pl-2">
							<v-radio-group v-model="importSubstanceType" row>
								<v-radio value="fito" :label="$gettext('Fitotherapy')"/>
								<v-radio value="mtc" :label="$gettext('TCM')"/>
							</v-radio-group>
						</v-col>
					</v-row>
				</v-row>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate key="general">General</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col cols="12" md="4">
										<v-text-field
											v-if="admin || isExternalEntity"
											:rules="requiredRule"
											v-model="product.information[language].name"
											:label="$gettext('Name')"
											outlined
											class="px-2"
										></v-text-field>
										<v-text-field
											v-else
											:rules="requiredRule"
											v-model="product.name"
											:label="$gettext('Name')"
											outlined
											class="px-2"
										></v-text-field>
									</v-col>
									<template v-if="type == 'compost'">
										<v-col cols="12" md="2">
											<v-text-field
												v-model="product.daily_dose"
												:rules="requiredRule"
												:label="$gettext('Daily Dose')"
												outlined
												class="px-2"
												type="number"
											></v-text-field>
										</v-col>
										<v-col cols="12" md="2">
											<v-select
												:rules="requiredRule"
												v-model="product.unit_id"
												:items="productUnits"
												:label="$gettext('Unit')"
												item-value="id"
												outlined
											>
												<template slot="selection" slot-scope="data">
													{{ data.item.name }} ({{ data.item.code }})
												</template>
												<template slot="item" slot-scope="data">
													{{ data.item.name }} ({{ data.item.code }})
												</template>
											</v-select>
										</v-col>
										<v-col cols="12" md="4">
											<v-text-field
												v-model="product.units_box"
												:label="$gettext('Doses per Box')"
												outlined
												class="px-2"
												type="number"
											></v-text-field>
										</v-col>
										<v-col cols="12" md="4">
											<v-select
												v-model="product.presentation_id"
												:items="productPresentations"
												:label="$gettext('Presentation')"
												item-text="name"
												item-value="id"
												class="px-2"
												outlined
											></v-select>
										</v-col>
										<v-col cols="12" md="4">
											<v-select
												v-model="product.storage_id"
												:items="productStorages"
												:label="$gettext('Storage')"
												item-text="name"
												item-value="id"
												outlined
											></v-select>
										</v-col>
										<v-col cols="12" md="8">
											<v-text-field
												v-if="admin || isExternalEntity"
												v-model="product.information[language].aditional_info"
												:label="$gettext('Aditional Information')"
												outlined
												class="pl-2"
											></v-text-field>
											<v-text-field
												v-else
												v-model="product.aditional_info"
												:label="$gettext('Aditional Information')"
												outlined
												class="pl-2"
											></v-text-field>
										</v-col>
									</template>
									<template v-else-if="type == 'complex_product'">
										<v-col cols="12">
											<v-btn small fab class="mb-3" color="primary" @click="createComponent">
												<v-icon>mdi-plus</v-icon>
											</v-btn>
											<v-btn v-if="num_products > 0" small fab class="mb-3 ml-2" color="primary" @click="removeComponent" :disabled="cantRemove">
												<v-icon>mdi-minus</v-icon>
											</v-btn>
										</v-col>
										<template v-if="num_products > 0">
											<v-row v-for="(n, i) in num_products" no-gutters :key="i">
												<v-col cols="12" md="4">
													<v-select
														v-model="product.components[i].presentation_id"
														:items="productPresentations"
														:label="$gettext('Presentation')"
														item-text="name"
														item-value="id"
														class="px-2"
														outlined
													></v-select>
												</v-col>
												<v-col cols="12" md="4">
													<v-select
														v-model="product.components[i].storage_id"
														:items="productStorages"
														:label="$gettext('Storage')"
														item-text="name"
														item-value="id"
														outlined
													></v-select>
												</v-col>
												<v-col cols="12" md="2">
													<v-text-field
														v-model="product.components[i].daily_dose"
														:rules="requiredRule"
														:label="$gettext('Daily Dose')"
														outlined
														class="px-2"
														type="number"
													></v-text-field>
												</v-col>
												<v-col cols="12" md="2">
													<v-select
														:rules="requiredRule"
														v-model="product.components[i].unit_id"
														:items="productUnits"
														:label="$gettext('Unit')"
														item-value="id"
														class="px-2"
														outlined
													>
														<template slot="selection" slot-scope="data">
															{{ data.item.name }} ({{ data.item.code }})
														</template>
														<template slot="item" slot-scope="data">
															{{ data.item.name }} ({{ data.item.code }})
														</template>
													</v-select>
												</v-col>
												<v-col cols="12" md="4">
													<v-text-field
														v-model="product.components[i].units_box"
														:label="$gettext('Doses per Box')"
														outlined
														class="px-2"
														type="number"
													></v-text-field>
												</v-col>
												<!-- <v-col cols="12" md="8">
													<v-text-field
														v-if="admin"
														v-model="product.components[i].information[language].aditional_info"
														:label="$gettext('Aditional Information')"
														outlined
														class="pr-2"
													></v-text-field>
													<v-text-field
														v-else
														v-model="product.components[i].aditional_info"
														:label="$gettext('Aditional Information')"
														outlined
														class="pr-2"
													></v-text-field>
												</v-col> -->
												<v-col cols="12">
													<v-divider class="mb-3"></v-divider>
												</v-col>
											</v-row>
										</template>
									</template>
									<template v-else>
										<v-col cols="12" md="4">
											<template v-if="type != 'complex_substance'">
											<v-text-field
												v-if="admin || isExternalEntity"
												v-model="product.information[language].scientific_name"
												:label="$gettext('Scientific Name')"
												:rules="requiredRule"
												outlined
											></v-text-field>
											<v-text-field
												v-else
												v-model="product.scientific_name"
												:label="$gettext('Scientific Name')"
												:rules="requiredRule"
												outlined
											></v-text-field>
											</template>
										</v-col>
										<v-col cols="12" md="4">
											<v-select
												:rules="requiredRule"
												v-model="product.substance_type_id"
												:items="substanceTypes"
												:label="$gettext('Substance Type')"
												item-text="name"
												item-value="id"
												outlined
												class="px-2"
											></v-select>
										</v-col>
										<template v-if="product.substance_type_id == 2">
											<v-col cols="12" md="4">
												<v-text-field
													v-if="admin || isExternalEntity"
													v-model="product.information[language].other_names"
													:label="$gettext('Aditional Names')"
													outlined
													class="px-2"
												></v-text-field>
											</v-col>
											<v-col cols="12" md="4">
												<v-text-field
													v-if="admin || isExternalEntity"
													v-model="product.other_scientific_names"
													:label="$gettext('Synonymy')"
													outlined
												></v-text-field>
											</v-col>
											<v-col cols="12" md="4">
												<v-select
													v-model="product.plant_part_id"
													:items="plantParts"
													:label="$gettext('Plant Part')"
													item-text="name"
													item-value="id"
													outlined
													class="px-2"
												></v-select>
											</v-col>
										</template>
									</template>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row v-if="type == 'compost' || type == 'complex_product'" no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate key="provider">Provider</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col cols="12" md="4">
										<v-select
											v-model="product.external_entity_id"
											:items="providers"
											:label="$gettext('Provider')"
											item-text="name"
											item-value="id"
											outlined
											class="px-2"
											:rules="requiredRule"
											:disabled="isExternalEntity"
										></v-select>
									</v-col>
									<v-col v-if="product.external_entity_id == -1" cols="12" md="4">
										<v-text-field
											:rules="requiredRule"
											v-model="new_provider"
											:label="$gettext('Name')"
											outlined
										></v-text-field>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row v-if="type == 'compost' || type == 'complex_substance'" no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate key="ingredients">Ingredients</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col cols="12" md="12" class="px-2">
										<SearchProducts context="new-product" type="supplement" :substance="true" @add-product="addSubstance" :availableProcessing="productProcessing" />
									</v-col>
									<template v-if="admin && edit">
										<v-btn
											color="primary"
											class="mt-3"
											@click="recalculateQuantities(product.ingredients, product.daily_dose)"
										>
											Recalcular quantidades
										</v-btn>
									</template>
									<v-col cols="12" md="12" class="mt-4">
										<SubstanceList
											:products="product.ingredients"
											context="new-product"
											type="supplement"
											@remove-product="removeSubstance"
											:availableProcessing="productProcessing"
										/>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row v-if="type == 'complex_product' && num_products > 0" no-gutters class="mt-4">
					<v-col cols="12" md="12" v-for="(n, i) in num_products" :key="i">
						<v-card>
							<v-card-title class="primary--text" key="ingredients"><translate>Ingredients</translate><span class="ml-2">- {{getPresentation(product.components[i].presentation_id)}}</span></v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col cols="12" md="12" class="px-2">
										<SearchProducts context="new-product" type="supplement" :substance="true" @add-product="addSubstanceToComplexProduct($event, i)" :availableProcessing="productProcessing" />
									</v-col>
									<template v-if="admin && edit">
										<v-btn
											color="primary"
											class="mt-3"
											@click="recalculateQuantities(product.components[i].ingredients, product.components[i].daily_dose)"
										>
											Recalcular quantidades
										</v-btn>
									</template>
									<v-col cols="12" md="12" class="mt-4">
										<SubstanceList
											:products="product.components[i].ingredients"
											context="new-product"
											type="supplement"
											@remove-product="removeSubstance($event, product.components[i])"
											:availableProcessing="productProcessing"
										/>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate key="indications">Indications</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col v-if="admin" cols="12" md="12" class="px-2">
										<v-textarea
											outlined
											:placeholder="$gettext('Write the product indications')"
											v-model="product.information[language].indications"
										></v-textarea>
									</v-col>
									<v-col cols="12" md="12" class="mt-4 px-2">
										<SearchICD :selectedICD="product.indications_icd" context="new-product" />
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate key="contraindications">Contraindications</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col v-if="admin" cols="12" md="12" class="px-2">
										<v-textarea
											outlined
											:placeholder="$gettext('Write the product contraindications')"
											v-model="product.information[language].contraindications"
										></v-textarea>
									</v-col>
									<v-col cols="12" md="12" class="mt-4 px-2">
										<SearchICD :selectedICD="product.contraindications_icd" context="new-product" />
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row v-if="type == 'compost'" no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate key="posology">Posology</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col cols="12" md="4">
										<v-select
											v-model="product.recommendation_quantity.posology_unit_id"
											:items="productUnits"
											:label="$gettext('Unit')"
											item-value="id"
											outlined
											hide-details
											class="px-2"
										>
											<template slot="selection" slot-scope="data">
												{{ data.item.name }} ({{ data.item.code }})
											</template>
											<template slot="item" slot-scope="data">
												{{ data.item.name }} ({{ data.item.code }})
											</template>
										</v-select>
									</v-col>
									<v-col cols="12" md="2" class="my-auto text-center">
										<span class="text-subtitle-1" v-translate>Daily Frequency:</span>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('PA')"
											outlined
											hide-details
											v-model="product.recommendation_quantity.morning"
											class="px-3"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('MM')"
											outlined
											hide-details
											v-model="product.recommendation_quantity.middle_morning"
											class="px-3"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('A')"
											outlined
											hide-details
											v-model="product.recommendation_quantity.half_day"
											class="px-3"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('MT')"
											outlined
											hide-details
											v-model="product.recommendation_quantity.middle_afternoon"
											class="px-3"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('J')"
											outlined
											hide-details
											v-model="product.recommendation_quantity.night"
											class="px-3"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label=">16 (Min)"
											outlined
											hide-details
											v-model="product.recommendation_quantity.over16_min"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="12-15 (Min)"
											outlined
											hide-details
											v-model="product.recommendation_quantity.between12_15_min"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="6-11 (Min)"
											outlined
											hide-details
											v-model="product.recommendation_quantity.between6_11_min"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="<6 (Min)"
											outlined
											hide-details
											v-model="product.recommendation_quantity.under6_min"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label=">16 (Max)"
											outlined
											hide-details
											v-model="product.recommendation_quantity.over16_max"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="12-15 (Max)"
											outlined
											hide-details
											v-model="product.recommendation_quantity.between12_15_max"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="6-11 (Max)"
											outlined
											hide-details
											v-model="product.recommendation_quantity.between6_11_max"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="<6 (Max)"
											outlined
											hide-details
											v-model="product.recommendation_quantity.under6_max"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											:label="$gettext('Pregnant')"
											outlined
											hide-details
											v-model="product.recommendation_quantity.pos_pregnant"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											:label="$gettext('Lactant')"
											outlined
											hide-details
											v-model="product.recommendation_quantity.pos_lactant"
											class="px-2"
										></v-text-field>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row v-if="type == 'complex_product' && num_products > 0" no-gutters class="mt-4">
					<v-col cols="12" md="12" v-for="(n, i) in num_products" :key="i">
						<v-card>
							<v-card-title class="primary--text" key="posology"><translate>Posology</translate><span class="ml-2">- {{getPresentation(product.components[i].presentation_id)}}</span></v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col cols="12" md="4">
										<v-select
											v-model="product.components[i].recommendation_quantity.posology_unit_id"
											:items="productUnits"
											:label="$gettext('Unit')"
											item-value="id"
											outlined
											hide-details
											class="px-2"
										>
											<template slot="selection" slot-scope="data">
												{{ data.item.name }} ({{ data.item.code }})
											</template>
											<template slot="item" slot-scope="data">
												{{ data.item.name }} ({{ data.item.code }})
											</template>
										</v-select>
									</v-col>
									<v-col cols="12" md="2" class="my-auto text-center">
										<span class="text-subtitle-1" v-translate>Daily Frequency:</span>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('PA')"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.morning"
											class="px-3"
											type="number"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('MM')"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.middle_morning"
											class="px-3"
											type="number"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('A')"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.half_day"
											class="px-3"
											type="number"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('MT')"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.middle_afternoon"
											class="px-3"
											type="number"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="1">
										<v-text-field
											:label="$gettext('J')"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.night"
											class="px-3"
											type="number"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label=">16 (Min)"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.over16_min"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="12-15 (Min)"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.between12_15_min"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="6-11 (Min)"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.between6_11_min"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="<6 (Min)"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.under6_min"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label=">16 (Max)"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.over16_max"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="12-15 (Max)"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.between12_15_max"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="6-11 (Max)"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.between6_11_max"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											label="<6 (Max)"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.under6_max"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											:label="$gettext('Pregnant')"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.pos_pregnant"
											class="px-2"
										></v-text-field>
									</v-col>
									<v-col cols="12" md="3" class="mt-5">
										<v-text-field
											:label="$gettext('Lactant')"
											outlined
											hide-details
											v-model.number="product.components[i].recommendation_quantity.pos_lactant"
											class="px-2"
										></v-text-field>
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row v-if="type == 'substance' || type == 'complex_substance'" no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-card>
							<v-card-title class="primary--text" v-translate key="drug-interactions">Drug Interactions</v-card-title>
							<v-card-text>
								<v-row no-gutters>
									<v-col v-if="admin" cols="12" md="12" class="px-2">
										<v-textarea
											outlined
											:placeholder="$gettext('Write the substance interactions')"
											v-model="product.information[language].interactions"
										></v-textarea>
									</v-col>
									<v-col cols="12" md="12" class="mt-4 px-2">
										<SearchDrugs :selectedDrugs="product.drugs" context="new-product" :drugInteractions="drugInteractions" />
									</v-col>
								</v-row>
							</v-card-text>
						</v-card>
					</v-col>
				</v-row>
				<v-row no-gutters class="mt-4">
					<v-col cols="12" md="12">
						<v-btn
							v-if="edit"
							color="primary"
							class="float-right ml-2"
							@click="editProduct"
							:loading="saving"
						>
							<translate>Save</translate>
						</v-btn>
						<v-btn
							v-else
							color="primary"
							class="float-right ml-2"
							@click="createProduct"
							:loading="saving"
						>
							<translate>Create</translate>
						</v-btn>
						<v-btn color="primary" class="float-right" :to="cancel">
							<translate>Cancel</translate>
						</v-btn>
					</v-col>
				</v-row>
			</v-form>
		</div>
	</div>
</template>

<script>
import SubstanceList from "@/components/products/SubstanceList.vue";
import SearchProducts from "@/components/products/SearchProducts.vue";
import SearchSubstances from "@/components/products/SearchSubstances.vue";
import SearchICD from "@/components/icd/SearchICD.vue";
import SearchDrugs from "@/components/drugs/SearchDrugs.vue";
import axios from "axios";
import Vue from 'vue';

export default {
	name: "ProductForm",
	props: {
		product: {
			default: () => ({
				name: null,
				scientific_name: null,
				other_scientific_names: null,
				revision: false,
				discontinued: false,
				visible: true,
				unit_id: null,
				daily_dose: 1,
				units_box: null,
				presentation_id: null,
				storage_id: null,
				aditional_info: null,
				external_entity_id: null,
				substance_type_id: null,
				plant_part_id: null,
				ingredients: [],
				indications_icd: [],
				contraindications_icd: [],
				components: [],
				drugs: [],
				information: {
					pt: {
						language: "pt",
						name: null,
						other_names: null,
						scientific_name: null,
						indications: null,
						contraindications: null,
						interactions: null,
						aditional_info: null
					},
					en: {
						language: "en",
						name: null,
						other_names: null,
						scientific_name: null,
						indications: null,
						contraindications: null,
						interactions: null,
						aditional_info: null
					},
					es: {
						language: "es",
						name: null,
						other_names: null,
						scientific_name: null,
						indications: null,
						contraindications: null,
						interactions: null,
						aditional_info: null
					}
				},
				recommendation_quantity: {
					posology_unit_id: 2,
					morning: null,
					middle_morning: null,
					half_day: null,
					middle_afternoon: null,
					night: null,
					over16_min: null,
					between12_15_min: null,
					between6_11_min: null,
					under6_min: null,
					over16_max: null,
					between12_15_max: null,
					between6_11_max: null,
					under6_max: null,
					pos_pregnant: null,
					pos_lactant: null
				}
			})
		},
		edit: {
			default: false
		},
		providers: {
			default: () => []
		},
		productUnits: {
			default: () => []
		},
		substanceTypes: {
			default: () => []
		},
		plantParts:{ 
			default: () => [] 
		},
		productStorages: {
			default: () => []
		},
		productPresentations: {
			default: () => []
		},
		productProcessing: {
			default: () => []
		},
		drugInteractions: {
			default: () => []
		},
		admin: {
			default: false
		}
	},
	components: {
		SubstanceList,
		SearchProducts,
		SearchSubstances,
		SearchICD,
		SearchDrugs
	},
	data: () => ({
		language: "pt",
		type: "compost",
		new_provider: null,
		saving: false,
		generalFormValid: false,
		success: false,
		error: false,
		errorMessage: "",
		associatedProducts: [],
		showAssociatedProducts: false,
		showImportProduct: false,
		showImportSubstance: false,
		num_products: 0,
		importSubstanceType: "fito"
	}),
	computed: {
		cancel() {
			if (this.admin) return { name: "admin-supplements" };
			else return { name: "supplements" };
		},
		requiredRule() {
			return [v => !!v || this.$gettext("Required")];
		},
		availableLanguages() {
			return [
				{ code: "en", name: "English" },
				{ code: "pt", name: "Português" },
				{ code: "es", name: "Espanol" }
			];
		},
		cantRemove() {
			if(this.num_products == 0) return false;
			if(this.product.components[this.num_products - 1].id) return true;
			return false;
		},
		isExternalEntity() {
			return this.$store.getters.isExternalEntity;
		}
	},
	methods: {
		addSubstance(substance) {
			//CALL API
			if (substance) {
				var exist = false;
				for (var i = 0; i < this.product.ingredients.length; i++) {
					if (this.product.ingredients[i].id == substance.product.id) {
						exist = true;
						break;
					}
				}
				if (!exist) {
					//CALL API
					console.log("ADD SUBSTANCE");
					//substance.ingredients = [];
					Vue.set(substance, 'ingredients', []);
					this.product.ingredients.push(substance);
				}
			}
		},
		addSubstanceToComplexProduct(substance, c) {
			//CALL API
			if (substance) {
				var exist = false;
				for (var i = 0; i < this.product.components[c].ingredients.length; i++) {
					if (this.product.components[c].ingredients[i].id == substance.product.id) {
						exist = true;
						break;
					}
				}
				if (!exist) {
					//CALL API
					console.log("ADD SUBSTANCE");
					//substance.ingredients = [];
					Vue.set(substance, 'ingredients', []);
					this.product.components[c].ingredients.push(substance);
				}
			}
		},
		removeSubstance(substance, product) {
			if(product) {
				product.ingredients.splice(product.ingredients.indexOf(substance), 1);
			} else {
				this.product.ingredients.splice(
					this.product.ingredients.indexOf(substance),
					1
				);
			}
		},
		editProduct() {
			if (this.validateForms()) {
				this.error = false;
				this.success = false;
				this.saving = true;
				const form = this.getForm();
				const productId = this.$route.params.sid;
				axios
					.post("/product/" + productId + "/edit", form)
					.then(response => {
						console.log(response.data);
						this.saving = false;
						if (response.data.result == "ok") {
							console.log(response.data);
							if (this.admin) {
								window.scrollTo(0, 0);
								this.success = true;
								if(response.data.product) this.product = response.data.product;
							} else {
								this.$router.push({
									name: "supplements"
								});
							}
							// this.$router.push({
							//   name: "edit-product",
							//   params: { sid: productId }
							// });
						} else {
							window.scrollTo(0, 0);
							this.error = true;
							this.errorMessage = response.data.error;
						}
					})
					.catch(error => {
						window.scrollTo(0, 0);
						this.saving = false;
						this.error = true;
						console.log(error);
					});
			} else {
				window.scrollTo(0, 0);
			}
		},
		createProduct() {
			this.error = false;
			this.success = false;
			if (this.validateForms()) {
				console.log("creating");
				this.saving = true;
				this.success = false;
				this.error = false;
				const form = this.getForm();
				axios
					.post("/product/new", form)
					.then(response => {
						this.saving = false;
						if (response.data === "OK") {
							console.log(response.data);
							if (this.admin) {
								window.scrollTo(0, 0);
								this.success = true;
								//this.$router.push({
								//   name: "edit-product",
								//   params: { sid: productId }
								//});
								this.$router.push({
									name: "admin-supplements",
									params: { admin: true }
								});
							} else {
								this.$router.push({
									name: "supplements"
								});
							}
						} else {
							window.scrollTo(0, 0);
							this.error = true;
							this.errorMessage = response.data.error;
						}
					})
					.catch(error => {
						window.scrollTo(0, 0);
						this.saving = false;
						this.error = true;
						console.log(error);
					});
			} else {
				window.scrollTo(0, 0);
			}
		},
		clickImport() {
			if(this.type == 'substance' || this.type == 'complex_substance') this.showImportSubstance = !this.showImportSubstance
			else this.showImportProduct = !this.showImportProduct
		},
		importProduct(productId) {
			console.log(productId);
			this.saving = true;
			let link;
			if(this.importSubstanceType == 'fito') link = "/product/" + productId + "/edit";
			else link = "/mtc/substances/" + productId + "/edit";
			axios
				.get(link)
				.then(response => {
					this.saving = false;
					if(this.importSubstanceType == 'fito') {
						let id = this.product.id;
						this.product = response.data.product;
						this.product.id = id;
					}
					else {
						console.log(response.data);
						this.product.indications_icd = response.data.product.indications_icd;
						this.product.contraindications_icd = response.data.product.contraindications_icd;
						this.product.drugs = response.data.product.drugs;
						this.product.information.pt.indications = response.data.product.information.pt.indications;
						this.product.information.pt.contraindications = response.data.product.information.pt.contraindications;
						this.product.information.pt.interactions = response.data.product.information.pt.interactions;
						this.product.information.en.indications = response.data.product.information.en.indications;
						this.product.information.en.contraindications = response.data.product.information.en.contraindications;
						this.product.information.en.interactions = response.data.product.information.en.interactions;
						this.product.information.es.indications = response.data.product.information.es.indications;
						this.product.information.es.contraindications = response.data.product.information.es.contraindications;
						this.product.information.es.interactions = response.data.product.information.es.interactions;
					}
				})
				.catch(error => {
					console.log(error);
					this.saving = false;
				});
		},
		getAssociatedProducts() {
			const productId = this.$route.params.sid;
			axios
				.get("/product/" + productId + "/associated_products")
				.then(response => {
					console.log(response.data);
					this.associatedProducts = response.data;
					this.showAssociatedProducts = true;
				})
				.catch(error => {
					console.log(error);
				});
		},
		validateForms() {
			return this.$refs.generalForm.validate();
		},
		getIngredients(list) {
			if(!list) return [];
			var ingredients = {};
			list.forEach(i => {
				ingredients[i.product.id] = { quantity: i.quantity, unit: i.unit, processing: i.processing, info: i.info, ingredients: this.getIngredients(i.ingredients) };
			});
			return ingredients;
		},
		getForm() {
			if(this.type == 'complex_product') return this.getComplexProductForm();
			const formData = new FormData();
			//if (this.admin && this.language) {
			//	formData.append("language", this.language);
			//}
			if (!this.edit) {
				var substance = false;
				var complex_substance = false;
				if (this.type === "substance") substance = true;
				if (this.type === "complex_substance") {
					substance = true;
					complex_substance = true;
				}
				formData.append("substance", substance);
				formData.append("complex_substance", complex_substance);
			} else {
				formData.append("substance", this.product.substance);
				if(this.product.complex_substance) formData.append("complex_substance", this.product.complex_substance);
			}
			if (this.product.name == null) {
				this.product.name = this.product.information.pt.name;
			}
			formData.append("name", this.product.name);
			formData.append("revision", this.product.revision);
			formData.append("discontinued", this.product.discontinued);
			formData.append("visible", this.product.visible);
			if (this.product.scientific_name) {
				formData.append("scientific_name", this.product.scientific_name);
			}
			if (this.product.other_scientific_names) {
				formData.append("other_scientific_names", this.product.other_scientific_names);
			}
			if (this.product.unit_id) {
				formData.append("product_unit_id", this.product.unit_id);
			}
			if (this.product.presentation_id) {
				formData.append("product_presentation_id", this.product.presentation_id);
			}
			if (this.product.storage_id) {
				formData.append("product_storage_id", this.product.storage_id);
			}
			if(this.product.daily_dose) {
				formData.append("daily_dose", this.product.daily_dose);
			}
			if (this.product.units_box) {
				formData.append("units_box", this.product.units_box);
			}
			if (this.product.aditional_info) {
				formData.append("aditional_info", this.product.aditional_info);
			}
			if (this.product.external_entity_id) {
				formData.append("provider", this.product.external_entity_id);
				if (this.product.external_entity_id == -1) {
					formData.append("new_provider", this.new_provider);
				}
			}
			if (this.product.substance_type_id) {
				formData.append("substance_type_id", this.product.substance_type_id);
			}
			if (this.product.plant_part_id) {
				formData.append("plant_part_id", this.product.plant_part_id);
			}
			//Posology frequency
			if (this.product.recommendation_quantity.morning != null) {
				formData.append(
					"morning",
					this.product.recommendation_quantity.morning
				);
			}
			if (this.product.recommendation_quantity.middle_morning != null) {
				formData.append(
					"middle_morning",
					this.product.recommendation_quantity.middle_morning
				);
			}
			if (this.product.recommendation_quantity.half_day != null) {
				formData.append(
					"half_day",
					this.product.recommendation_quantity.half_day
				);
			}
			if (this.product.recommendation_quantity.middle_afternoon != null) {
				formData.append(
					"middle_afternoon",
					this.product.recommendation_quantity.middle_afternoon
				);
			}
			if (this.product.recommendation_quantity.night != null) {
				formData.append("night", this.product.recommendation_quantity.night);
			}
			//Posology limitations
			if (this.product.recommendation_quantity.over16_min != null) {
				formData.append(
					"over16_min",
					this.product.recommendation_quantity.over16_min
				);
			}
			if (this.product.recommendation_quantity.over16_max != null) {
				formData.append(
					"over16_max",
					this.product.recommendation_quantity.over16_max
				);
			}
			if (this.product.recommendation_quantity.under6_max != null) {
				formData.append(
					"under6_max",
					this.product.recommendation_quantity.under6_max
				);
			}
			if (this.product.recommendation_quantity.under6_min != null) {
				formData.append(
					"under6_min",
					this.product.recommendation_quantity.under6_min
				);
			}
			if (this.product.recommendation_quantity.between12_15_min != null) {
				formData.append(
					"between12_15_min",
					this.product.recommendation_quantity.between12_15_min
				);
			}
			if (this.product.recommendation_quantity.between12_15_max != null) {
				formData.append(
					"between12_15_max",
					this.product.recommendation_quantity.between12_15_max
				);
			}
			if (this.product.recommendation_quantity.between6_11_min != null) {
				formData.append(
					"between6_11_min",
					this.product.recommendation_quantity.between6_11_min
				);
			}
			if (this.product.recommendation_quantity.between6_11_max != null) {
				formData.append(
					"between6_11_max",
					this.product.recommendation_quantity.between6_11_max
				);
			}
			if (this.product.recommendation_quantity.pos_pregnant != null) {
				formData.append(
					"pos_pregnant",
					this.product.recommendation_quantity.pos_pregnant
				);
			}
			if (this.product.recommendation_quantity.pos_lactant != null) {
				formData.append(
					"pos_lactant",
					this.product.recommendation_quantity.pos_lactant
				);
			}
			if (this.product.recommendation_quantity.posology_unit_id != null) {
				formData.append(
					"posology_unit_id",
					this.product.recommendation_quantity.posology_unit_id
				);
			}
			var ingredients = {};
			ingredients = this.getIngredients(this.product.ingredients)
			formData.append("ingredients_json", JSON.stringify(ingredients));
			//Indications
			var indications_icd = [];
			this.product.indications_icd.forEach(icd => {
				indications_icd.push({disease_id: icd.disease_id, degree: icd.degree});
			});
			formData.append("indications_json", JSON.stringify(indications_icd));
			//Contraindications
			var contraindications_icd = [];
			this.product.contraindications_icd.forEach(icd => {
				contraindications_icd.push({disease_id: icd.disease_id, degree: icd.degree});
			});
			formData.append(
				"contraindications_json",
				JSON.stringify(contraindications_icd)
			);
			var drugs = [];
			this.product.drugs.forEach(d => {
				drugs.push(d);
			});
			formData.append("drugs_json", JSON.stringify(drugs));
			//Information
			var information = [];
			for (const [key, value] of Object.entries(this.product.information)) {
				console.log(key);
				information.push(value);
			}
			formData.append("information_json", JSON.stringify(information));

			console.log(
				information,
				ingredients,
				indications_icd,
				contraindications_icd,
				drugs
			);
			return formData;
		},
		getComplexProductForm() {
			const formData = new FormData();
			formData.append("complex_product", true);
			formData.append("substance", false);
			formData.append("complex_substance", false);
			if (this.product.name == null) {
				this.product.name = this.product.information.pt.name;
			}
			formData.append("name", this.product.name);
			formData.append("revision", this.product.revision);

			if (this.product.external_entity_id) {
				formData.append("provider", this.product.external_entity_id);
				if (this.product.external_entity_id == -1) {
					formData.append("new_provider", this.new_provider);
				}
			}
			//Components
			let components = [];
			for(let i = 0; i < this.num_products; i++) {
				let p = this.product.components[i];
				let product = {};
				if(p.id) product.id = p.id;
				product.unit_id = p.unit_id;
				product.daily_dose = p.daily_dose;
				product.units_box = p.units_box;
				product.presentation_id = p.presentation_id;
				product.storage_id = p.storage_id;
				product.recommendation_quantity = p.recommendation_quantity;
				let ingredients = this.getIngredients(this.product.components[i].ingredients);
				product.ingredients = ingredients;
				components.push(product);
			}
			formData.append("components_json", JSON.stringify(components));
			//Indications
			var indications_icd = [];
			this.product.indications_icd.forEach(icd => {
				indications_icd.push({disease_id: icd.disease_id, degree: icd.degree});
			});
			formData.append("indications_json", JSON.stringify(indications_icd));
			//Contraindications
			var contraindications_icd = [];
			this.product.contraindications_icd.forEach(icd => {
				contraindications_icd.push({disease_id: icd.disease_id, degree: icd.degree});
			});
			formData.append("contraindications_json", JSON.stringify(contraindications_icd));
			//Information
			var information = [];
			for (const [key, value] of Object.entries(this.product.information)) {
				console.log(key);
				information.push(value);
			}
			formData.append("information_json", JSON.stringify(information));

			formData.append("ingredients_json", '{}');
			formData.append("drugs_json", '[]');
			
			for (var pair of formData.entries()) {
					console.log(pair[0]+ ', ' + pair[1]); 
			}
			return formData
		},
		createComponent() {
			this.num_products += 1;
			this.$set(this.product.components, this.num_products - 1,
				{
					unit_id: null,
					daily_dose: 1,
					units_box: null,
					presentation_id: null,
					storage_id: null,
					aditional_info: null,
					information: {
						pt: {
							language: "pt",
							aditional_info: null
						},
						en: {
							language: "en",
							aditional_info: null
						},
						es: {
							language: "es",
							aditional_info: null
						}
					},
					ingredients: [],
					recommendation_quantity: {
						posology_unit_id: 2,
						morning: null,
						middle_morning: null,
						half_day: null,
						middle_afternoon: null,
						night: null,
						over16_min: null,
						between12_15_min: null,
						between6_11_min: null,
						under6_min: null,
						over16_max: null,
						between12_15_max: null,
						between6_11_max: null,
						under6_max: null,
						pos_pregnant: null,
						pos_lactant: null
					}
				})
		},
		removeComponent() {
			if(this.product.components[this.num_products - 1].id) console.log("cant remove");
			else this.num_products -= 1;
		},
		getPresentation(id) {
			for(let i = 0; i < this.productPresentations.length; i++) {
				if(this.productPresentations[i].id == id) return this.productPresentations[i].name;
			}
			return null;
		},
		recalculateQuantities(ingredients, daily_dose) {
			console.log(ingredients, daily_dose);
			for(let i = 0; i < ingredients.length; i++) {
				ingredients[i].quantity = ingredients[i].quantity * daily_dose;
			}
		}
	},
	mounted() {
		if(this.edit && this.product.complex_product) this.num_products = this.product.components.length;
	},
	created() {
		if(!this.edit && this.isExternalEntity) {
			this.product.external_entity_id = this.$store.getters.externalEntity.id;
		}
	},
	watch: {
		product: {
			immediate: true,
			handler() {
				if (this.edit) {
					if (!this.product.substance == true) {
						if(this.product.complex_product) this.type = "complex_product";
						else this.type = "compost";
					} else {
						if (!this.product.complex_substance == true) this.type = "substance";
						else this.type = "complex_substance";
					}
				}
			}
		},
		language(value) {
			if (!value) this.language = "pt";
		}
	}
};
</script>